import React, { useState, useEffect } from 'react';
import api from '../services/api';
import BarcodeScanner from '../components/BarcodeScanner';
import { generateItemBarcodeLabels } from '../utils/pdfGenerator';
import '../styles/PrintLabels.css';

const PrintLabels = () => {
  const [allItems, setAllItems] = useState([]);
  const [labelItems, setLabelItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [itemSearch, setItemSearch] = useState('');
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllItems();
  }, []);

  const fetchAllItems = async () => {
    try {
      setLoading(true);
      const response = await api.get('/items');
      setAllItems(response.data);
    } catch (err) {
      console.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = allItems.filter(item => {
    const text = [item.name, item.part_number].join(' ').toLowerCase();
    const qtyStr = item.qty_per_case ? String(item.qty_per_case) : '';
    const words = itemSearch.toLowerCase().trim().split(/\s+/);
    if (words.length === 1) {
      return text.includes(words[0]) || qtyStr === words[0];
    }
    return words.every(word => text.includes(word) || qtyStr === word);
  });

  const handleSelectItem = (item) => {
    setSelectedItemId(item.id);
    setItemSearch(`${item.part_number} - ${item.name}`);
    setShowItemDropdown(false);
  };

  const handleAddItem = () => {
    if (!selectedItemId || quantity < 1) return;

    const item = allItems.find(i => i.id === parseInt(selectedItemId));
    if (!item) return;

    // Check if item already in list
    const existingIndex = labelItems.findIndex(li => li.item_id === item.id);
    if (existingIndex >= 0) {
      // Update quantity
      const updated = [...labelItems];
      updated[existingIndex].quantity += quantity;
      setLabelItems(updated);
    } else {
      // Add new item
      setLabelItems([...labelItems, {
        item_id: item.id,
        name: item.name,
        part_number: item.part_number,
        quantity: quantity
      }]);
    }

    setSelectedItemId('');
    setItemSearch('');
    setQuantity(1);
  };

  const handleRemoveItem = (index) => {
    const updated = [...labelItems];
    updated.splice(index, 1);
    setLabelItems(updated);
  };

  const handleUpdateQuantity = (index, newQty) => {
    // Allow empty string for typing, but don't allow negative
    if (newQty !== '' && newQty < 1) return;
    const updated = [...labelItems];
    updated[index].quantity = newQty;
    setLabelItems(updated);
  };

  const handleBarcodeScan = (barcode) => {
    setShowScanner(false);
    const scannedLower = barcode.toLowerCase().trim();

    // Find item by part_number or barcode
    const item = allItems.find(i =>
      i.part_number?.toLowerCase() === scannedLower ||
      i.barcode?.toLowerCase() === scannedLower
    );

    if (item) {
      setSelectedItemId(item.id);
      setItemSearch(`${item.part_number} - ${item.name}`);
      setShowItemDropdown(false);
    } else {
      setItemSearch(barcode);
      setShowItemDropdown(true);
      alert(`Item not found for barcode: ${barcode}`);
    }
  };

  const getTotalLabels = () => {
    return labelItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  const handlePrintLabels = () => {
    if (labelItems.length === 0) {
      alert('Please add at least one item to print labels');
      return;
    }

    // Expand items based on quantity (each label is one item)
    const expandedItems = [];
    labelItems.forEach(item => {
      for (let i = 0; i < item.quantity; i++) {
        expandedItems.push({
          name: item.name,
          part_number: item.part_number
        });
      }
    });

    generateItemBarcodeLabels(expandedItems);
  };

  const handleClearAll = () => {
    if (labelItems.length > 0 && window.confirm('Clear all items?')) {
      setLabelItems([]);
    }
  };

  if (loading) {
    return (
      <div className="print-labels-page">
        <div className="loading">Loading items...</div>
      </div>
    );
  }

  return (
    <div className="print-labels-page">
      <div className="page-header">
        <div className="header-top">
          <h1>Print Labels</h1>
          {labelItems.length > 0 && (
            <button className="print-button" onClick={handlePrintLabels}>
              Print {getTotalLabels()} Labels
            </button>
          )}
        </div>
        <p className="page-subtitle">Select items and quantities to generate barcode labels</p>
      </div>

      <div className="labels-container">
        <div className="add-item-section">
          <h3>Add Items</h3>
          <div className="add-item-form">
            <div className="item-search-container">
              <div className="unified-search-box">
                <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                </svg>
                <input
                  type="text"
                  value={itemSearch}
                  onChange={(e) => {
                    setItemSearch(e.target.value);
                    setSelectedItemId('');
                    setShowItemDropdown(true);
                  }}
                  onFocus={() => setShowItemDropdown(true)}
                  className="item-search-input"
                  placeholder="Search or scan item..."
                />
                <button
                  type="button"
                  className="scan-btn"
                  onClick={() => setShowScanner(true)}
                  title="Scan item barcode"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2 4h2v16H2V4zm4 0h1v16H6V4zm3 0h2v16H9V4zm4 0h1v16h-1V4zm3 0h1v16h-1V4zm3 0h2v16h-2V4z"/>
                  </svg>
                </button>
                <button
                  type="button"
                  className="dropdown-toggle"
                  onClick={() => setShowItemDropdown(!showItemDropdown)}
                >
                  ▼
                </button>
              </div>
              {showItemDropdown && (
                <div className="item-dropdown">
                  {filteredItems.length === 0 ? (
                    <div className="dropdown-empty">No items found</div>
                  ) : (
                    filteredItems.slice(0, 15).map(item => (
                      <div
                        key={item.id}
                        className="dropdown-item"
                        onClick={() => handleSelectItem(item)}
                      >
                        <span className="dropdown-part">{item.part_number}</span>
                        <span className="dropdown-name">{item.name}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <div className="qty-control">
              <button
                type="button"
                className="qty-btn minus"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
              >
                -
              </button>
              <input
                type="number"
                className="qty-input"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                onBlur={(e) => {
                  const val = parseInt(e.target.value) || 1;
                  setQuantity(Math.max(1, val));
                }}
                min="1"
              />
              <button
                type="button"
                className="qty-btn plus"
                onClick={() => setQuantity(quantity + 1)}
              >
                +
              </button>
            </div>
            <button
              className="add-item-btn"
              onClick={handleAddItem}
              disabled={!selectedItemId}
            >
              Add
            </button>
          </div>
        </div>

        <div className="label-items-section">
          <div className="section-header">
            <h3>Items to Print ({labelItems.length})</h3>
            {labelItems.length > 0 && (
              <button className="clear-btn" onClick={handleClearAll}>
                Clear All
              </button>
            )}
          </div>

          {labelItems.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🏷️</div>
              <p className="empty-text">No items added</p>
              <p className="empty-subtext">Search or scan items above to add them to your label list</p>
            </div>
          ) : (
            <>
              <div className="label-items-table-container">
                <table className="label-items-table">
                  <thead>
                    <tr>
                      <th>Part #</th>
                      <th>Name</th>
                      <th>Labels</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {labelItems.map((item, index) => (
                      <tr key={index}>
                        <td className="part-number">{item.part_number}</td>
                        <td className="item-name">{item.name}</td>
                        <td>
                          <div className="qty-control inline">
                            <button
                              type="button"
                              className="qty-btn minus"
                              onClick={() => handleUpdateQuantity(index, Math.max(1, item.quantity - 1))}
                            >
                              -
                            </button>
                            <input
                              type="number"
                              className="qty-input"
                              value={item.quantity}
                              onChange={(e) => {
                                const val = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                                handleUpdateQuantity(index, val);
                              }}
                              onBlur={(e) => {
                                const val = parseInt(e.target.value) || 1;
                                handleUpdateQuantity(index, Math.max(1, val));
                              }}
                              min="1"
                            />
                            <button
                              type="button"
                              className="qty-btn plus"
                              onClick={() => handleUpdateQuantity(index, item.quantity + 1)}
                            >
                              +
                            </button>
                          </div>
                        </td>
                        <td>
                          <button
                            className="remove-item-btn"
                            onClick={() => handleRemoveItem(index)}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="labels-summary">
                <div className="summary-row">
                  <span>Total Items:</span>
                  <span>{labelItems.length}</span>
                </div>
                <div className="summary-row total">
                  <span>Total Labels:</span>
                  <span>{getTotalLabels()}</span>
                </div>
                <p className="summary-note">
                  Labels per sheet: 30 (Avery 5160)
                </p>
              </div>

              <div className="print-actions">
                <button className="print-btn" onClick={handlePrintLabels}>
                  Print {getTotalLabels()} Labels
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <BarcodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScan}
        title="Scan Item Barcode"
      />
    </div>
  );
};

export default PrintLabels;
